import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { MutationRefetchQueries, ValidationError } from "~/common/types";
import {
  CreateDataImportProjectDocument,
  type CreateDataImportProjectMutation,
  type CreateDataImportProjectMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { CreateDataImportProjectFormData } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface Props {
  refetchQueriesOnCreate: MutationRefetchQueries;
  onSuccess: () => void;
  onValidationError: (error: Pick<ValidationError, "fields">) => void;
}

export function useCreateDataImportProject({ refetchQueriesOnCreate, onSuccess, onValidationError }: Props) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const { tenantId } = useTenant();
  const [createDataImportProject] = useMutation<
    CreateDataImportProjectMutation,
    CreateDataImportProjectMutationVariables
  >(CreateDataImportProjectDocument, {
    refetchQueries: refetchQueriesOnCreate,
  });

  const submit = useCallback(
    async (data: CreateDataImportProjectFormData) => {
      const progressToastId = toast.loading(t("ui.processing"));
      setProcessing(true);

      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const resp = await createDataImportProject({
          variables: {
            input: {
              tenantId,
              name: data.name,
              year: data.year ? Number(data.year) : null,
              userIds: data.users.map((user) => user.id),
              documentUrl: data.documentUrl,
              description: data.description,
            },
          },
        });

        switch (resp.data?.cnutCreateDataImportProject.__typename) {
          case "DataImportProjectCreateOutput":
            toast.success(
              t("feature.common.createdSuccessfully", {
                model: "dataImportProject",
              }),
            );
            onSuccess();
            break;
          case "ValidationError":
            onValidationError(resp.data.cnutCreateDataImportProject);
            break;
          default:
            throw new Error("Invalid response type");
        }
      } catch (e) {
        if (e instanceof Error) {
          toast.error(translateError(t, e));
        }
        throw e;
      } finally {
        toast.dismiss(progressToastId);
        setProcessing(false);
      }
    },
    [createDataImportProject, onSuccess, onValidationError, t, tenantId],
  );

  return {
    submit,
    processing,
  };
}
